import { post, get, put, del } from "../api.helper/root";

import { buildQueryParams } from "@/lib/utils";

import {
  IAddStaffPayload,
  IEditStaffPayload,
  IUpdateUserGroupsPayload,
  IStaffStatusUpdatePayload,
  StaffStatusQueryKey,
  IResendInviteLinkPayload,
} from "@/types/identity";
import {
  IAddGroupPermissionsPayload,
  ICreateGroupPayload,
  IDeleteGroupPayload,
  IEditGroupNamePayload,
  IRemoveGroupPermissionsPayload,
} from "@/types/access";
import {
  IAddModulesToBranchPayload,
  ICreateBranchDetailsPayload,
  IUpdateBranchDetailsPayload,
  IUpdateBranchStatusPayload
} from "@/types/branch";

const identityUri = process.env.VUE_APP_IDENTITY_BASE_URL;

const getStaffsUri = identityUri + "/user-management/get-practitioners";
const getGroupsUri = identityUri + "/access-management/get-groups";
const getGroupUri = identityUri + "/access-management/get-single-groups";
const createGroupUri = identityUri + "/access-management/create-group";
const deleteGroupUri = identityUri + "/access-management/delete-group";
const editGroupNameUri = identityUri + "/access-management/edit-group-name";
const getGroupPermsUri =
  identityUri + "/access-management/get-permissions-by-groupId";
const addGroupPermsUri =
  identityUri + "/access-management/add-group-permissions";
const removeGroupPermsUri =
  identityUri + "/access-management/remove-group-permissions";
const getSubModulePermsUri =
  identityUri + "/access-management/get-permissions-by-submoduleID";
const getSubModulesUri = `${identityUri}/access-management/get-submodules`;
const updateStaffStatusUri = `${identityUri}/user-management/activate-suspend-practitioner`;
const addStaffUri = `${identityUri}/user-management/add-practitioner`;
const editUserUri = `${identityUri}/user-management/edit-practitioner`;
const addUserToGroupUri = `${identityUri}/user-management/add-practitioner-to-group`;
const removeUserFromGroupUri = `${identityUri}/user-management/remove-practitioner-from-group`;
const resendInviteLinkUri = `${identityUri}/user-management/resend-invite`;
const getBranchUri = `${identityUri}/branch-setup/get-branch`;
const createBranchUri = `${identityUri}/branch-setup/create-branch`;
const updateBranchUri = `${identityUri}/branch-setup/update-branch`;
const getBranchOperationalSummaryUri = `${identityUri}/branch-setup/operational-summary`;
const deactivateBranchUri = `${identityUri}/branch-setup/deactivate-branch`;
const reactivateBranchUri = `${identityUri}/branch-setup/reactivate-branch`;
const shutdownBranchUri = `${identityUri}/branch-setup/shutdown-branch`;
const addModulesToBranchUri = `${identityUri}/branch-setup/add-module-to-branch`;
const removeMemberFromBranchUri = `${identityUri}/branch-setup/remove-member`;

interface IGetStaffsParams {
  pageNumber?: number;
  pageSize?: number;
  accountStatus?: StaffStatusQueryKey;
  isVerification?: boolean;
  getExpiredLicense?: boolean;
  orderByQueryString?: string;
  searchQuery?: string;
  tenantId: string;
}

interface IGetGroupsParams {
  pageNumber?: number;
  pageSize?: number;
  searchQuery?: string;
  tenantId: string;
}

interface IGetSubModulesParams {
  pageNumber?: number;
  pageSize?: number;
  tenantId: string;
}

interface IGetSubModulePermsParams {
  pageNumber?: number;
  pageSize?: number;
  submoduleID?: number;
  tenantId: string;
}

interface IGetBranchesParams {
  tenantId: string;
  pageNumber?: number;
  pageSize?: number;
  branchName?: string;
  branchId?: number;
}

export const getStaffs = ({
  pageNumber,
  pageSize,
  accountStatus,
  isVerification,
  getExpiredLicense,
  orderByQueryString,
  searchQuery,
  tenantId}: IGetStaffsParams) =>
  get(
    `${getStaffsUri}${buildQueryParams({
      PageNumber: pageNumber,
      PageSize: pageSize,
      AccountStatus: accountStatus,
      IsVerification: isVerification,
      GetExpiredLicense: getExpiredLicense,
      OrderByQueryString: orderByQueryString,
      SearchString: searchQuery,
    })}`,
    {
      headers: {
        tenantId
      }
    }
  );

export const addStaff = (tenantId: string, userData: IAddStaffPayload) =>
  post(addStaffUri, userData, {
    headers: {
      tenantId
    }
  });

export const getGroups = ({ pageNumber, pageSize, tenantId, searchQuery }: IGetGroupsParams) =>
  get(
    `${getGroupsUri}${buildQueryParams({
      pageId: pageNumber,
      pageSize: pageSize,
      SearchString: searchQuery,
    })}`,
    {
      headers: {
        tenantId
      }
    }
  );

export const getGroupById = ({ groupId, tenantId }: { groupId: number, tenantId: string }) => {
  return get(`${getGroupUri}?groupID=${groupId}`, { headers: { tenantId } });
}

export const createGroup = (userData: ICreateGroupPayload, tenantId: string) =>
  post(createGroupUri, userData, {
    headers: {
      tenantId
    }
  });

export const deleteGroup = (userData: IDeleteGroupPayload, tenantId: string) =>
  del(deleteGroupUri, userData, {
    headers: {
      tenantId
    }
  });

export const editGroupName = (userData: IEditGroupNamePayload, groupId: number, tenantId: string) =>
  put(`${editGroupNameUri}?groupID=${groupId}`, userData, {
    headers: {
      tenantId
    }
  });

export const getGroupPerms = (pageNumber: number, pageSize: number, groupId: number, tenantId: string) =>
  get(
    `${getGroupPermsUri}${buildQueryParams({
      pageId: pageNumber,
      PageSize: pageSize,
      groupID: groupId,
    })}`,
    {
      headers: {
        tenantId
      }
    }
  );

export const addGroupPerms = (userData: IAddGroupPermissionsPayload, tenantId: string) =>
  put(addGroupPermsUri, userData, {
    headers: {
      tenantId
    }
  });

export const removeGroupPerms = (userData: IRemoveGroupPermissionsPayload, tenantId: string) =>
  del(removeGroupPermsUri, userData, {
    headers: {
      tenantId
    }
  });

export const getSubModulePerms = ({
  pageNumber,
  pageSize,
  submoduleID,
  tenantId
}: IGetSubModulePermsParams) =>
  get(
    `${getSubModulePermsUri}${buildQueryParams({
      pageId: pageNumber,
      pageSize: pageSize,
      submoduleID: submoduleID,
    })}`,
    { headers: { tenantId } }
  );

export const getSubModules = ({pageNumber, pageSize, tenantId}: IGetSubModulesParams) =>
  get(
    `${getSubModulesUri}${buildQueryParams({
      pageId: pageNumber,
      pageSize: pageSize,
    })}`,
    { headers: { tenantId } }
  );

export const staffStatusUpdate = (userData: IStaffStatusUpdatePayload, tenantId: string) =>
  put(updateStaffStatusUri, userData, {
    headers: {
      tenantId
    }
  });

export const editUser = (userData: IEditStaffPayload, tenantId: string) =>
  put(editUserUri, userData, {
    headers: {
      tenantId
    }
  });

export const addUserToGroup = (userData: IUpdateUserGroupsPayload, tenantId: string) =>
  post(addUserToGroupUri, userData, {
    headers: {
      tenantId
    }
  });

export const removeUserFromGroup = (userData: IUpdateUserGroupsPayload, tenantId: string) =>
  del(removeUserFromGroupUri, userData, {
    headers: {
      tenantId
    }
  });

export const resendInviteLink = (userData: IResendInviteLinkPayload, tenantId: string) =>
  put(resendInviteLinkUri, userData, {
    headers: {
      tenantId
    }
  });

export const getBranches = ({
  tenantId,
  pageNumber = 1,
  pageSize = 10,
  branchName = "",
  branchId
}: IGetBranchesParams) =>
    get(`${getBranchUri}${buildQueryParams({
      pageNumber: pageNumber,
      pageSize: pageSize,
      BranchName: branchName,
      BranchId: branchId,
    })}`,
    {
      headers: { tenantId }
    }
  );

export const createBranch = (userData: ICreateBranchDetailsPayload, tenantId: string) => 
  post(createBranchUri, userData, {
    headers: {
      tenantId
    }
  });

export const updateBranch = (branchId: number, userData: IUpdateBranchDetailsPayload, tenantId: string) =>
  put(`${updateBranchUri}?branchId=${branchId}`, userData, {
    headers: { tenantId }
  });

export const getBranchOperationalSummary = (tenantId: string) =>
  get(getBranchOperationalSummaryUri, {
    headers: { tenantId }
  });

export const deactivateBranch = (userData: IUpdateBranchStatusPayload, tenantId: string) =>
  post(deactivateBranchUri, userData, {
    headers: { tenantId }
  });

export const reactivateBranch = (userData: IUpdateBranchStatusPayload, tenantId: string) =>
  post(reactivateBranchUri, userData, {
    headers: { tenantId }
  });

export const shutdownBranch = (userData: IUpdateBranchStatusPayload, tenantId: string) =>
  post(shutdownBranchUri, userData, {
    headers: { tenantId }
  });

export const addModulesToBranch = (userData: IAddModulesToBranchPayload, tenantId: string) =>
  post(addModulesToBranchUri, userData, {
    headers: { tenantId }
  });

export const removeMemberFromBranch = (branchId: number, practitionerId: string, tenantId: string) =>
  del(`${removeMemberFromBranchUri}?branchId=${branchId}&practitionerId=${practitionerId}`, {
    headers: { tenantId }
  });